import { useRef, useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminAuthProvider, useAdminAuth } from '../context/AdminAuthContext';

function AdminLoginContent() {
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, sendResetPassword, configError } = useAdminAuth();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setLoading(true);
    const form = e.currentTarget;
    const email = (form.elements.namedItem('email') as HTMLInputElement).value;
    const password = (form.elements.namedItem('password') as HTMLInputElement).value;
    const result = await login(email, password);
    if (result.ok) {
      navigate('/admin');
      return;
    }
    setError(result.error || 'Invalid email or password. Please try again.');
    setLoading(false);
  };

  const handleResetPassword = async (form: HTMLFormElement) => {
    const email = (form.elements.namedItem('email') as HTMLInputElement).value;
    if (!email) {
      setError('Enter your email first, then request a password reset.');
      return;
    }

    setError('');
    setNotice('');
    const result = await sendResetPassword(email);
    if (result.ok) {
      setNotice(result.message || 'Password reset email sent.');
    } else {
      setError(result.error || 'Unable to send password reset email.');
    }
  };

  return (
    <div className="min-h-screen bg-[#0D1B2A] flex items-center justify-center px-6">
      <div className="absolute inset-0 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=dark%20navy%20deep%20ocean%20water%20abstract%20texture%20minimal%20dark%20blue%20waves%20pattern%20professional%20moody%20atmosphere%20high%20resolution%20seamless%20background%20very%20dark%20almost%20black&width=1440&height=900&seq=admin-login-bg&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0D1B2A] via-[#0D1B2A]/95 to-[#111D2B]"></div>
      </div>

      <div className="relative z-10 w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="https://static.readdy.ai/image/3bab8804b943b0f9d2e99ce4e4e8123d/a7b2a29c0c21ce8564d3744200077816.png"
            alt="Stingray"
            className="h-10 w-auto object-contain brightness-0 invert mx-auto mb-4"
          />
          <div
            className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full"
            style={{ backgroundColor: 'color-mix(in srgb, var(--accent) 20%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 30%, transparent)', color: 'var(--accent)' }}
          >
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-shield-keyhole-line"></i>
            </div>
            Admin Panel
          </div>
        </div>

        {/* Card */}
        <div className="bg-[#1C2B3A] border border-white/10 rounded-xl p-8">
          <h1 className="text-xl font-black text-white mb-1">Welcome back</h1>
          <p className="text-white/40 text-sm mb-6">Sign in to access the admin panel</p>

          <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-white/60 text-xs font-semibold uppercase tracking-wide mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-line text-white/30 text-sm"></i>
                </div>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="admin@stingray.com"
                  className="w-full bg-[#0D1B2A] border border-white/10 text-white text-sm pl-10 pr-4 py-3 rounded-lg focus:outline-none placeholder-white/20 transition-colors"
                  style={{ '--tw-ring-color': 'var(--accent)' } as React.CSSProperties}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                />
              </div>
            </div>

            <div>
              <label className="block text-white/60 text-xs font-semibold uppercase tracking-wide mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-lock-line text-white/30 text-sm"></i>
                </div>
                <input
                  type={showPass ? 'text' : 'password'}
                  name="password"
                  required
                  placeholder="••••••••"
                  className="w-full bg-[#0D1B2A] border border-white/10 text-white text-sm pl-10 pr-10 py-3 rounded-lg focus:outline-none placeholder-white/20 transition-colors"
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-white/30 hover:text-white cursor-pointer transition-colors"
                >
                  <i className={showPass ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2.5">
                <div className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className="ri-error-warning-line"></i>
                </div>
                {error}
              </div>
            )}

            {notice && (
              <div className="flex items-center gap-2 text-green-400 text-xs bg-green-500/10 border border-green-500/20 rounded-lg px-3 py-2.5">
                <div className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className="ri-check-line"></i>
                </div>
                {notice}
              </div>
            )}

            {configError && (
              <div className="flex items-center gap-2 text-amber-300 text-xs bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2.5">
                <div className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className="ri-alert-line"></i>
                </div>
                {configError}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || Boolean(configError)}
              className="w-full text-white font-bold py-3.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm uppercase tracking-wide disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ backgroundColor: 'var(--accent)' }}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin"></i>
                  </div>
                  Signing in...
                </>
              ) : (
                <>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-login-box-line"></i>
                  </div>
                  Sign In
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                if (formRef.current) {
                  void handleResetPassword(formRef.current);
                }
              }}
              className="w-full text-[#F97316] text-xs font-semibold hover:underline cursor-pointer"
            >
              Send password reset email
            </button>
          </form>

          <div className="mt-5 bg-[#0D1B2A] border border-white/5 rounded-lg px-4 py-3">
            <p className="text-white/30 text-xs font-semibold uppercase tracking-wide mb-2">cPanel Admin Access</p>
            <p className="text-white/40 text-xs">
              Create the admin account in the PHP backend config or seed data, then sign in here.
            </p>
          </div>
        </div>

        <p className="text-center text-white/20 text-xs mt-6">
          &copy; {new Date().getFullYear()} Stingray &mdash; Admin Access Only
        </p>
      </div>
    </div>
  );
}

export default function AdminLoginPage() {
  return (
    <AdminAuthProvider>
      <AdminLoginContent />
    </AdminAuthProvider>
  );
}
